import React, { ChangeEvent, FormEvent, useCallback, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  styled,
} from '@mui/material';

import { regions as allRegions } from '@luxuryescapes/lib-regions/lib/regions';

import PlaceLookUp from '~/components/Common/Forms/PlaceLookUp';
import Image from '~/components/Common/Image';
import { TRENDING_PLACES_BRANDS } from '~/components/Content/TrendingPlaces/TrendingPlacesPage';
import { TYPEAHEAD_PLACE_TYPES } from '~/components/Content/TrendingPlaces/constants';

import { uploadImage } from '~/services/ImageService';
import SearchService, { TypeaheadSearchResult } from '~/services/SearchService';

type Props = {
  onClose: () => void;
};

const VisuallyHiddenInput = styled('input')({
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const StyledButton = styled(IconButton)`
  margin-left: 10px;
`;

function AddTrendingPlaceForm({ onClose }: Props) {
  const [place, setPlace] = useState<TypeaheadSearchResult | null>(null);
  const [manualPosition, setManualPosition] = useState<number | null>(null);
  const [imageId, setImageId] = useState<string | null>(null);
  const [brands, setBrands] = useState<Array<string>>([]);
  const [regions, setRegions] = useState<Array<string>>([]);
  const [enforcedPosition, setEnforcedPosition] = useState<boolean>(false);
  const allRegionCodes = allRegions['luxuryescapes'].map((region) => region.code);
  const allBrandValues = TRENDING_PLACES_BRANDS.map((region) => region.value);
  const allRegionsSelected = regions.length === allRegionCodes.length;
  const allBrandsSelected = brands.length === allBrandValues.length;
  const regionOptions = [
    { name: allRegionsSelected ? 'Deselect All' : 'Select All', code: 'selectAll' },
    ...allRegions['luxuryescapes'],
  ];
  const brandOptions = [
    { name: allBrandsSelected ? 'Deselect All' : 'Select All', value: 'selectAll' },
    ...TRENDING_PLACES_BRANDS,
  ];
  const [failedItem, setFailedItem] = useState<Array<{ brand: string; region: string }>>([]);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);

  const handleSelectedPlace = useCallback((selection: TypeaheadSearchResult | undefined) => {
    setPlace(selection);
    setImageId(selection?.format.imageId ?? null);
  }, []);

  const onImageChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files[0];
    const res = await uploadImage(file);
    setImageId(res.public_id);
  };

  const handleManualPositionChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    if (inputValue === '') {
      setManualPosition(null);
    } else if (Number.isInteger(Number(inputValue)) && Number(inputValue) >= 1) {
      setManualPosition(Number(inputValue));
    } else {
      setManualPosition(null);
    }
  };

  const handleBrandChange = (_: React.SyntheticEvent, selectedBrands: Array<(typeof brandOptions)[number]>) => {
    const selectedBrandValues = selectedBrands.map((brand) => brand.value);

    setBrands(
      selectedBrandValues.includes('selectAll')
        ? brands.length === allBrandValues.length
          ? []
          : allBrandValues
        : selectedBrandValues,
    );
  };

  const handleRegionChange = (_: React.SyntheticEvent, selectedRegions: Array<(typeof regionOptions)[number]>) => {
    const selectedRegionCodes = selectedRegions.map((region) => region.code);

    setRegions(
      selectedRegionCodes.includes('selectAll')
        ? regions.length === allRegionCodes.length
          ? []
          : allRegionCodes
        : selectedRegionCodes,
    );
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    const failedToAdd: Array<{ brand: string; region: string }> = [];
    for (const region of regions.filter((region) => region !== 'selectAll')) {
      for (const brand of brands.filter((region) => region !== 'selectAll')) {
        try {
          const { status } = await SearchService.upsertTrendingPlace(
            place.id,
            brand,
            region,
            imageId,
            manualPosition ?? undefined,
            enforcedPosition,
          );

          if (status !== 200) {
            failedToAdd.push({
              brand: TRENDING_PLACES_BRANDS.find((item) => item.value === brand).name,
              region: allRegions['luxuryescapes'].find((item) => item.code === region).name,
            });
          }
        } catch {
          failedToAdd.push({ brand, region });
        }
      }
    }

    if (failedToAdd.length > 0) {
      setFailedItem(failedToAdd);
      return;
    }

    setSuccessDialogOpen(true);
  };

  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);
    onClose();
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 3 }}>
      <form onSubmit={handleSubmit}>
        <Stack direction="column" gap={4}>
          <PlaceLookUp onCurrentValueChange={handleSelectedPlace} types={TYPEAHEAD_PLACE_TYPES} label="Place" />

          <TextField
            label="Manual Position (Optional)"
            type="number"
            value={manualPosition || ''}
            onChange={handleManualPositionChange}
            inputProps={{ min: 1, step: 1 }}
            variant="outlined"
            helperText="A manual position of 1 is at the top of the results. Leave blank to use the impression score."
            fullWidth
          />

          <FormControlLabel
            control={<Checkbox checked={enforcedPosition} onChange={(e) => setEnforcedPosition(e.target.checked)} />}
            label="Enforce Position"
          />

          <Autocomplete
            fullWidth
            multiple
            options={regionOptions}
            value={allRegions['luxuryescapes'].filter((region) => regions.includes(region.code))}
            onChange={handleRegionChange}
            getOptionLabel={(region) => region.name}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            filterSelectedOptions
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox checked={option.code === 'selectAll' ? allRegionsSelected : selected} sx={{ mr: 1 }} />
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Select Regions (Required)" required={regions.length === 0} />
            )}
          />

          <Autocomplete
            fullWidth
            multiple
            options={brandOptions}
            value={TRENDING_PLACES_BRANDS.filter((brand) => brands.includes(brand.value))}
            onChange={handleBrandChange}
            getOptionLabel={(brand) => brand.name}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            filterSelectedOptions
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox checked={option.value === 'selectAll' ? allBrandsSelected : selected} sx={{ mr: 1 }} />
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Select Brands (Required)" required={brands.length === 0} />
            )}
          />

          {!imageId && (
            <Box display="flex" justifyContent="flex-start">
              <Button variant="outlined" component="label">
                Upload Image
                <VisuallyHiddenInput onChange={(event) => onImageChange(event)} type="file" />
              </Button>
            </Box>
          )}

          <Box display="flex" alignItems="center" gap={2}>
            {imageId && <Image options={{ width: 150, dpr: 3 }} publicId={imageId} loading="lazy" />}
            {imageId && (
              <StyledButton sx={{ height: 10 }} onClick={() => setImageId(undefined)}>
                <CloseIcon />
              </StyledButton>
            )}
          </Box>
          {!!failedItem.length && (
            <Alert severity="error">
              <strong>Failed to add the following combinations:</strong>
              <ul style={{ margin: '0', padding: '0', listStyle: 'none' }}>
                {failedItem.map((item, index) => (
                  <li key={index}>
                    <strong> - Brand:</strong> {item.brand}
                    <strong> | Region:</strong> {item.region}
                  </li>
                ))}
              </ul>
            </Alert>
          )}
          <Stack direction="column" gap={2}>
            <Button variant="contained" color="primary" type="submit">
              Submit
            </Button>
            <Button variant="outlined" color="primary" onClick={onClose}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      </form>

      <Dialog open={successDialogOpen} onClose={handleSuccessDialogClose}>
        <DialogTitle>Success</DialogTitle>
        <DialogContent>
          <Alert severity="success">
            <strong>Successfully added place:</strong> {place?.format?.mainText}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSuccessDialogClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default AddTrendingPlaceForm;
